import { Template } from '@zeus/shared';

// Constants
const DEFAULT_FONTWEIGHT = 400;
const DEFAULT_FONTSTYLE = 'normal';
const DEFAULT_LETTERSPACING = 1;
const DEFAULT_LINEHEIGHT = 1;

const DEFAULT_TEXT_FORMATE = {
  fontWeight: DEFAULT_FONTWEIGHT,
  letterSpacing: DEFAULT_LETTERSPACING,
  lineHeight: DEFAULT_LINEHEIGHT,
  fontStyle: DEFAULT_FONTSTYLE,
};

// Common structures
const DEFAULT_BRAND_LOGO = {
  label: 'Brand logo',
  isEnabled: true,
  image: '',
};

const DEFAULT_CATEGORY = {
  color: '',
  ...DEFAULT_TEXT_FORMATE,
};

const DEFAULT_ITEM = {
  itemName: {
    color: '',
    ...DEFAULT_TEXT_FORMATE,
  },
  itemPrice: {
    color: '',
    isEnabled: true,
    ...DEFAULT_TEXT_FORMATE,
  },
  itemDescription: {
    isEnabled: true,
    color: '',
    ...DEFAULT_TEXT_FORMATE,
  },
  itemImage: {
    isEnabled: true,
  },
};

const DEFAULT_ITEM_DESCRIPTION = {
  label: 'Item descriptions',
  isEnabled: true,
};

const DEFAULT_ITEM_PRICE = {
  label: 'Item price',
  isEnabled: true,
};

const DEFAULT_BACKGROUND = {
  label: 'Background',
  isEnabled: true,
  color: '',
  image: '',
};

// Font URLs

// Template factory function
const FONTS = {
  MANROPE: 'https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap',
  POPPINS:
    'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  ROBOTO: 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap',
  BEBAS_NEUE: 'https://fonts.googleapis.com/css2?family=Bebas+Neue:wght@400&display=swap',
};

const FONTS_NAME = {
  MANROPE: 'Manrope',
  POPPINS: 'Poppins',
  ROBOTO: 'Roboto',
};

// Template factory function
interface TemplateConfig {
  templateId: number;
  fontFamily: string;
  itemsLimit: number;
  minItemsLimit: number;
  maxItemsLimit: number;
  orientation: 'Portrait' | 'Landscape';
  categoryLimit?: number;
  heading?: any;
  categoryUnderlineColor?: string;
  brandLogoLabel?: string;
  taxLabel?: {
    isEnabled: boolean;
    taxText: string;
  };
  background?: {
    label: string;
    isEnabled: boolean;
    color: string;
    image: string;
  };
  fontFamilyName?: string;
  itemImages?: {
    isEnabled: boolean;
    overflowImages: boolean;
  };
  categoryBackgroundColor?: string;
}

function createTemplate(config: TemplateConfig): Template {
  const {
    templateId,
    fontFamily,
    itemsLimit,
    minItemsLimit,
    maxItemsLimit,
    orientation,
    categoryLimit,
    heading,
    categoryUnderlineColor,
    brandLogoLabel = 'Brand logo',
    background = DEFAULT_BACKGROUND,
    fontFamilyName,
    itemImages = {
      isEnabled: false,
      overflowImages: false,
    },
    categoryBackgroundColor,
  } = config;

  return {
    templateId,
    fontFamily,
    fontFamilyName,
    itemsLimit,
    minItemsLimit,
    maxItemsLimit,
    ...(categoryLimit && { categoryLimit }),
    ...(heading && { heading }),
    brandLogo: {
      ...DEFAULT_BRAND_LOGO,
      label: brandLogoLabel,
    },
    category: {
      ...DEFAULT_CATEGORY,
      ...(categoryUnderlineColor !== undefined && { underlineColor: categoryUnderlineColor }),
      ...(categoryBackgroundColor !== undefined && { backgroundColor: categoryBackgroundColor }),
    },
    item: DEFAULT_ITEM,
    itemDescription: DEFAULT_ITEM_DESCRIPTION,
    itemPrice: DEFAULT_ITEM_PRICE,
    content: [],
    background,
    orientation,
    itemImages,
  };
}

// Template definitions - now much more concise
const template1Props: Template = createTemplate({
  templateId: 1,
  fontFamily: FONTS.MANROPE,
  itemsLimit: 29,
  minItemsLimit: 29,
  maxItemsLimit: 48,
  orientation: 'Landscape',
  categoryUnderlineColor: '',
  brandLogoLabel: 'Logo',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

const template2Props: Template = createTemplate({
  templateId: 2,
  fontFamily: FONTS.POPPINS,
  itemsLimit: 27,
  minItemsLimit: 27,
  maxItemsLimit: 70,
  orientation: 'Landscape',
  categoryUnderlineColor: '',
  brandLogoLabel: 'Logo',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
  categoryBackgroundColor: '#3C3333',
});

const template3Props: Template = createTemplate({
  templateId: 3,
  fontFamily: FONTS.MANROPE,
  itemsLimit: 20,
  minItemsLimit: 20,
  maxItemsLimit: 43,
  categoryLimit: 3,
  orientation: 'Landscape',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

const template4Props: Template = createTemplate({
  templateId: 4,
  fontFamily: FONTS.ROBOTO,
  itemsLimit: 30,
  minItemsLimit: 30,
  maxItemsLimit: 60,
  orientation: 'Portrait',
  heading: {
    color: '#510B03',
    isEnabled: true,
    value: 'The Daily Dash',
    key: 'heading',
    style: {},
  },
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

const template5Props: Template = createTemplate({
  templateId: 5,
  fontFamily: FONTS.MANROPE,
  itemsLimit: 27,
  minItemsLimit: 27,
  maxItemsLimit: 51,
  orientation: 'Portrait',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

const template6Props: Template = createTemplate({
  templateId: 6,
  fontFamily: FONTS.MANROPE,
  itemsLimit: 15,
  minItemsLimit: 15,
  maxItemsLimit: 23,
  orientation: 'Portrait',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

const template8Props: Template = createTemplate({
  templateId: 8,
  fontFamily: FONTS.MANROPE,
  itemsLimit: 7,
  minItemsLimit: 4,
  maxItemsLimit: 7,
  orientation: 'Portrait',
  itemImages: {
    overflowImages: true,
    isEnabled: true,
  },
});
const template7Props: Template = createTemplate({
  templateId: 7,
  fontFamily: FONTS.BEBAS_NEUE,
  itemsLimit: 10,
  minItemsLimit: 6,
  maxItemsLimit: 10,
  orientation: 'Landscape',
  taxLabel: {
    isEnabled: false,
    taxText: '',
  },
});

export const templatesProps: Record<number, Template> = {
  1: template1Props,
  2: template2Props,
  3: template3Props,
  4: template4Props,
  5: template5Props,
  6: template6Props,
  7: template7Props,
  8: template8Props,
};

// Alternative: Even more concise - inline object array
export const templatesPropsAlternative: Record<number, Template> = {
  1: createTemplate({
    templateId: 1,
    fontFamily: FONTS.MANROPE,
    fontFamilyName: FONTS_NAME.MANROPE,
    itemsLimit: 29,
    minItemsLimit: 29,
    maxItemsLimit: 48,
    orientation: 'Landscape',
    categoryUnderlineColor: '',
    brandLogoLabel: 'Logo',
  }),
  2: createTemplate({
    templateId: 2,
    fontFamily: FONTS.POPPINS,
    fontFamilyName: FONTS_NAME.POPPINS,
    itemsLimit: 27,
    minItemsLimit: 27,
    maxItemsLimit: 70,
    orientation: 'Landscape',
    categoryUnderlineColor: '',
    brandLogoLabel: 'Logo',
  }),
  3: createTemplate({
    templateId: 3,
    fontFamily: FONTS.MANROPE,
    fontFamilyName: FONTS_NAME.MANROPE,
    itemsLimit: 20,
    minItemsLimit: 20,
    maxItemsLimit: 43,
    categoryLimit: 3,
    orientation: 'Landscape',
  }),
  4: createTemplate({
    templateId: 4,
    fontFamily: FONTS.ROBOTO,
    fontFamilyName: FONTS_NAME.ROBOTO,
    itemsLimit: 30,
    minItemsLimit: 30,
    maxItemsLimit: 60,
    orientation: 'Portrait',
    heading: {
      color: '#510B03',
      isEnabled: true,
      value: 'The Daily Dash',
      key: 'heading',
      style: {},
    },
  }),
  5: createTemplate({
    templateId: 5,
    fontFamily: FONTS.MANROPE,
    fontFamilyName: FONTS_NAME.MANROPE,
    itemsLimit: 27,
    minItemsLimit: 27,
    maxItemsLimit: 51,
    orientation: 'Portrait',
  }),
  6: createTemplate({
    templateId: 6,
    fontFamily: FONTS.MANROPE,
    fontFamilyName: FONTS_NAME.MANROPE,
    itemsLimit: 15,
    minItemsLimit: 15,
    maxItemsLimit: 23,
    orientation: 'Portrait',
  }),
  8: createTemplate({
    templateId: 8,
    fontFamily: FONTS.MANROPE,
    itemsLimit: 7,
    minItemsLimit: 4,
    maxItemsLimit: 7,
    orientation: 'Portrait',
    background: {
      ...DEFAULT_BACKGROUND,
      color: '#FFEFD4',
    },
  }),
};
